import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router";
import truncate from "lodash/truncate";
import { addYears, startOfDay } from "date-fns";
import { Box, Typography, useTheme } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { useAccountsId, useDeleteAccount } from "@bn/api/apiv1";
import { useAccount } from "@api/v1/hooks/useAccount";
import { useUpdateAccount } from "@api/v1/hooks/useAccounts";
import {
  PrimaryActionWithMenu,
  ConfirmDeleteDialog,
  CardPageLayout,
  CardHeading,
  AccountsHeader,
} from "@components";
import AccountPreviewIcon from "@components/Accounts/AccountPreviewCard/AccountPreviewIcon";
import { InvestmentsChartDataStatistic } from "@api/v1/hooks/performance/useInvestmentsChartData";
import {
  DepotsDashboardChart,
  DepotsDashboardContent,
} from "../components/DepotsDashboard";
import { DepotMenu } from "./components/DepotMenu";

const depotChartFirstDate = startOfDay(addYears(new Date(), -1));

export const DepotPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { depotId } = useParams<"depotId">();
  const parsedDepotId = Number.parseInt(depotId!, 10);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [statistic, setStatistic] = useState<InvestmentsChartDataStatistic>();

  const { data: account } = useAccountsId({
    id: parsedDepotId,
  });
  const { mutateAsync, isPending: isDeleteRequestLoading } = useDeleteAccount();
  const { mutateAsync: updateMutateAsync } = useUpdateAccount();
  const [deleteContraAccount, setDeleteContraAccount] = useState(false);

  const contraAccountId = account?.contraAccountId;
  const { data: contraAccountData } = useAccount(contraAccountId);

  const canDeleteContraAccount =
    contraAccountData?.depotIds && contraAccountData?.depotIds?.length === 1;

  useEffect(() => {
    if (canDeleteContraAccount) setDeleteContraAccount(true);
  }, [canDeleteContraAccount]);

  const handleOpenDeleteModal = () => {
    setDeleteModalShow(true);
  };
  const handleCloseDeleteModal = () => setDeleteModalShow(false);

  const handleDeleteAccount = async () => {
    await mutateAsync({ id: parsedDepotId, deleteContraAccount });
    navigate("/depots-accounts");
  };

  const handleHideAccount = async () => {
    await updateMutateAsync({
      id: parsedDepotId,
      isHidden: !account!.isHidden,
      type: account!.type!,
      name: account!.name,
    });
    navigate("/depots-accounts");
  };

  const breadcrumbs: BreadcrumbItem[] = [
    {
      text: t("breadcrumbs.home"),
      to: "/",
    },
    {
      text: t("breadcrumbs.depotsAccounts"),
      to: "/depots-accounts",
    },
    {
      text: truncate(account?.name),
    },
  ];

  return (
    <CardPageLayout
      data-testid="single-depot-page"
      breadcrumbs={breadcrumbs}
      cardHeading={
        <CardHeading
          title={
            <AccountsHeader
              title={account?.name ?? ""}
              subtitle={account?.bankName || t("accountBox.noBank")}
              icon={
                account ? (
                  <AccountPreviewIcon
                    isReference={Boolean(account.depotIds?.length ?? 0)}
                    iconUrl={account.iconUrl}
                    type={account.type}
                    size={48}
                  />
                ) : (
                  <></>
                )
              }
            />
          }
          actions={
            <PrimaryActionWithMenu
              primaryActionText={t("depotPage.headerMenu.upload")}
              primaryActionUrl={
                account?.id ? `/depots/${account.id}/file-drop` : undefined
              }
            >
              <DepotMenu
                account={account}
                handleHideAccount={handleHideAccount}
                handleOpenDeleteModal={handleOpenDeleteModal}
              />
            </PrimaryActionWithMenu>
          }
          actionsInline
        />
      }
      cardChildren={
        <DepotsDashboardChart
          accountIds={[parsedDepotId]}
          rangeId="depot"
          rangeFirstDate={depotChartFirstDate}
          onStatistic={setStatistic}
          showComparisonSelector
        />
      }
    >
      <ConfirmDeleteDialog
        isOpen={deleteModalShow}
        title={t("depotPage.deleteModal.question")}
        onClose={handleCloseDeleteModal}
        onDelete={handleDeleteAccount}
        isDeleteRequestLoading={isDeleteRequestLoading}
      >
        <Box display="flex" alignItems="center" mt={2}>
          <Checkbox
            sx={{ paddingLeft: 0 }}
            onChange={() => setDeleteContraAccount((prevState) => !prevState)}
            checked={deleteContraAccount}
            disabled={!canDeleteContraAccount}
          />
          <Typography color={theme.palette.text.secondary}>
            {t("depotPage.deleteModal.deleteContraAccount")}
          </Typography>
        </Box>
      </ConfirmDeleteDialog>
      {statistic && (
        <DepotsDashboardContent
          accountIds={[parsedDepotId]}
          statistic={statistic}
          depotId={parsedDepotId}
        />
      )}
    </CardPageLayout>
  );
};
